import { useMemberSearch } from "@gymflow/api";
import { LeadDTO } from "@gymflow/types";
import { capitalize } from "lodash";
import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { useDebounceValue, useOnClickOutside } from "usehooks-ts";

import searchIcon from "../../../assets/img/search-lg.svg";
import { SidebarToggleContext } from "../../providers/SidebarToggleContext";
import useGymflowModels from "../../store";
import { Badge, Spinner } from "../atoms";
import { LeadActionsDropdown, UserActionsDropdown } from "../molecules";
import { UserMemberAvatar } from "../molecules/UserMemberAvatar";

export interface SearchGlobalProps {}

export const SearchGlobal: React.FC<SearchGlobalProps> = () => {
  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebounceValue(query, 400);
  const { api } = useGymflowModels();

  const { data, isLoading } = useMemberSearch({
    api,
    term: debouncedQuery,
    size: 5,
  });

  const ref = useRef(null);

  useOnClickOutside(
    ref,
    () => {
      setTimeout(() => {
        setQuery("");
      }, 100);
    },
    "mouseup",
  );

  const { hideSidebar } = useContext(SidebarToggleContext);

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setQuery(newValue);
  }, []);

  return (
    <div
      className="bg-gray-0 relative flex size-full flex-row items-center gap-x-2 px-8"
      ref={ref}
    >
      <img src={searchIcon} alt="search-icon" className="h-5" />
      <input
        value={query}
        onChange={handleInputChange}
        placeholder="Search..."
        type="search"
        className="bg-gray-0 placeholder:text-primary-700 flex size-full outline-none placeholder:text-base"
      />
      {!!query && isLoading && (
        <div className="bg-gray-0 absolute left-0 top-[calc(100%+.25rem)] z-[1000] flex w-full flex-row items-center justify-between gap-x-2 rounded-lg border border-gray-300 p-4 shadow">
          <Spinner className="" />
        </div>
      )}

      {!!query && !isLoading && data && data?.length === 0 && (
        <div className="bg-gray-0 absolute left-0 top-[calc(100%+.25rem)] z-[1000] flex w-full flex-row items-center justify-between gap-x-2 rounded-lg border border-gray-300 p-4 shadow">
          No results found
        </div>
      )}
      {!!query && !isLoading && data && data.length > 0 && (
        <div
          onClick={() => {
            setQuery("");
          }}
          className="bg-gray-0 absolute left-0 top-[calc(100%+.25rem)] z-[1000] flex w-fit min-w-full max-w-[100vw] flex-col rounded-lg border border-gray-300 shadow"
        >
          {data.map((user) => {
            return (
              <div
                key={user.id}
                className="flex flex-row items-center justify-between gap-x-2 p-4"
                onClick={() => {
                  hideSidebar();
                }}
              >
                <UserMemberAvatar className="w-full" {...user} />
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className="flex flex-row items-center justify-end gap-x-2"
                >
                  <Badge
                    intent={
                      (
                        {
                          LEAD: "primary",
                          USER: "secondary",
                        } as const
                      )[user.profileType]
                    }
                  >
                    {capitalize(user.profileType)}
                  </Badge>
                  {typeof user.id === "number" ? (
                    <LeadActionsDropdown
                      className="size-9"
                      lead={user as LeadDTO} // If id is number -> it's lead
                      leadName={`${user.firstName} ${user.lastName}`}
                    />
                  ) : (
                    <UserActionsDropdown
                      className="size-9"
                      memberId={user.id}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
